import { useEffect, useState } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Login } from "./auth/Login";
import { api } from "./api/client";
import { ToastProvider } from "./ui/toast";
import { useTheme } from "./ui/useTheme";
import {
  IconLogout, IconMoon, IconPackage, IconShield, IconSun, IconTag, IconUsers,
} from "./ui/icons";
import ArvetioLogo from "./ArvetioLogo.jsx";
import { Clients } from "./pages/Clients";
import { Demo } from "./pages/Demo";
import { Promos } from "./pages/Promos";
import { Orders } from "./pages/Orders";
import { Releases } from "./pages/Releases";
import { Pricing } from "./pages/Pricing";
import { LicenseTerms } from "./pages/LicenseTerms";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { ReturnPolicy } from "./pages/ReturnPolicy";
import { SecurityPolicy } from "./pages/SecurityPolicy";
import { SrpProcedure } from "./pages/SrpProcedure";
import { Admins } from "./pages/Admins";
import { nbsp } from "./utils/nbsp";

type Backend = "online" | "offline" | "unknown";

function useBackendStatus(): Backend {
  const [status, setStatus] = useState<Backend>("unknown");
  useEffect(() => {
    let alive = true;
    const ping = () =>
      api.health()
        .then(() => alive && setStatus("online"))
        .catch(() => alive && setStatus("offline"));
    ping();
    const id = window.setInterval(ping, 60_000);
    return () => { alive = false; window.clearInterval(id); };
  }, []);
  return status;
}

const NAV = [
  { to: "/klienci", label: "Licencje", Icon: IconUsers },
  { to: "/demo", label: "Licencje DEMO", Icon: IconTag },
  { to: "/zamowienia", label: "Zamówienia", Icon: IconTag },
  { to: "/kody", label: "Kody promocyjne", Icon: IconTag },
  { to: "/aplikacja", label: "Aplikacja / pobieranie", Icon: IconPackage },
  { to: "/cennik", label: "Cennik i regulamin", Icon: IconTag },
  { to: "/warunki", label: "Warunki licencji", Icon: IconShield },
  { to: "/prywatnosc", label: "Polityka prywatności", Icon: IconShield },
  { to: "/zwroty", label: "Warunki zwrotu i odstąpienie", Icon: IconShield },
  { to: "/bezpieczenstwo", label: "Bezpieczeństwo i podatności", Icon: IconShield },
  { to: "/srp", label: "Procedura zgłaszania (SRP)", Icon: IconShield },
  { to: "/administratorzy", label: "Administratorzy", Icon: IconShield },
];

function Shell() {
  const { instance } = useMsal();
  const [theme, toggleTheme] = useTheme();
  const backend = useBackendStatus();
  const account = instance.getActiveAccount();
  const [navOpen, setNavOpen] = useState(false); // mobilny drawer sidebara

  return (
    <div className="app">
      {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} />}
      <aside className={`sidebar${navOpen ? " open" : ""}`}>
        <div className="brand brand-arvetio">
          <ArvetioLogo variant="admin" size={30} wordmark onDark />
        </div>
        <nav className="nav">
          <div className="nav-group-label">Zarządzanie</div>
          {NAV.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} onClick={() => setNavOpen(false)}
                     className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="status-pill">
          <span className={`dot dot-${backend}`} />
          {backend === "online" ? "API online" : backend === "offline" ? "API niedostępne" : "Sprawdzanie API…"}
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="btn icon nav-toggle" title="Menu" onClick={() => setNavOpen((v) => !v)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <Routes>
            <Route path="/klienci" element={<TopTitle title="Licencje" sub="Licencje komercyjne: klucze, odnowienia, cofnięcia" />} />
            <Route path="/demo" element={<TopTitle title="Licencje DEMO" sub="Wydawanie kluczy DEMO (7 dni, 1 witryna + 3 OneDrive) i rejestr nadużyć" />} />
            <Route path="/zamowienia" element={<TopTitle title="Zamówienia" sub="Zamówienia sprzedażowe i akceptacja warunków przez klientów" />} />
            <Route path="/kody" element={<TopTitle title="Kody promocyjne" sub="Rabaty procentowe na zakup w portalu (newsletter, media społecznościowe)" />} />
            <Route path="/aplikacja" element={<TopTitle title="Aplikacja / pobieranie" sub="Link do pobrania, bieżący build i historia" />} />
            <Route path="/cennik" element={<TopTitle title="Cennik i regulamin" sub="Ceny za 1, 3 i 12 miesięcy oraz regulamin — serwowane przez /config" />} />
            <Route path="/warunki" element={<TopTitle title="Warunki licencji" sub="Treść akceptowana przez użytkownika aplikacji" />} />
            <Route path="/prywatnosc" element={<TopTitle title="Polityka prywatności" sub="Treść akceptowana przez użytkownika aplikacji" />} />
            <Route path="/zwroty" element={<TopTitle title="Warunki zwrotu i prawo odstąpienia" sub="Treść akceptowana przez klienta przy zakupie w portalu" />} />
            <Route path="/bezpieczenstwo" element={<TopTitle title="Bezpieczeństwo i podatności" sub="Publiczna polityka CVD + security.txt + okres wsparcia (CRA)" />} />
            <Route path="/srp" element={<TopTitle title="Procedura zgłaszania (SRP)" sub="Wewnętrzny runbook zgłaszania do ENISA (CRA art. 14) — niepublikowany" />} />
            <Route path="/administratorzy" element={<TopTitle title="Administratorzy" sub="Kto ma dostęp do konsoli licencji" />} />
            <Route path="*" element={<TopTitle title="" sub="" />} />
          </Routes>
          <div className="topbar-spacer" />
          <button className="btn icon" title="Motyw" onClick={toggleTheme}>
            {theme === "light" ? <IconMoon size={17} /> : <IconSun size={17} />}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>{account?.username}</span>
          <button className="btn icon" title="Wyloguj" onClick={() => instance.logoutPopup()}>
            <IconLogout size={17} />
          </button>
        </header>

        <div className="content">
          <div className="content-pad">
            <Routes>
              <Route path="/" element={<Navigate to="/klienci" replace />} />
              <Route path="/klienci" element={<Clients />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/zamowienia" element={<Orders />} />
              <Route path="/kody" element={<Promos />} />
              <Route path="/aplikacja" element={<Releases />} />
              <Route path="/cennik" element={<Pricing />} />
              <Route path="/warunki" element={<LicenseTerms />} />
              <Route path="/prywatnosc" element={<PrivacyPolicy />} />
              <Route path="/zwroty" element={<ReturnPolicy />} />
              <Route path="/bezpieczenstwo" element={<SecurityPolicy />} />
              <Route path="/srp" element={<SrpProcedure />} />
              <Route path="/administratorzy" element={<Admins />} />
              <Route path="*" element={<Navigate to="/klienci" replace />} />
            </Routes>
          </div>
        </div>
      </main>
    </div>
  );
}

function TopTitle({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <div className="topbar-title">{nbsp(title)}</div>
      <div className="topbar-sub">{nbsp(sub)}</div>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AuthenticatedTemplate>
        <Shell />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </ToastProvider>
  );
}
